import { createContext, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarCheck, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const BookingCtx = createContext(null);
export const useBooking = () => useContext(BookingCtx);

const SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00"];
const SERVICES = ["BPO & Operations", "AI Moderation & Clippers", "Custom Automation", "Enterprise LMS", "Web Design", "Media Strategy"];

export const BookingProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", date: "", time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time) return toast.error("Please pick a time slot.");
    setLoading(true);
    try {
      await api.post("/bookings", form);
      toast.success("Consultation requested. We'll confirm your slot by email.");
      setForm({ name: "", email: "", company: "", service: "", date: "", time: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BookingCtx.Provider value={{ openBooking: () => setOpen(true) }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="booking-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg bg-[#0C0C0C] border border-white/10 rounded-sm p-8 max-h-[92vh] overflow-y-auto"
            >
              <button onClick={() => setOpen(false)} data-testid="booking-close" className="absolute top-5 right-5 text-white/40 hover:text-white transition-colors">
                <X size={20} />
              </button>
              <div className="flex items-center gap-2 mb-2">
                <CalendarCheck size={16} className="text-cyan-400" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">Book a Consultation</span>
              </div>
              <h3 className="font-serif text-3xl text-white mb-6">Reserve time with our team.</h3>

              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="booking-form">
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Name</Label>
                  <Input required value={form.name} onChange={set("name")} data-testid="booking-name" className="bg-[#050505] border-white/15 h-11 rounded-sm focus-visible:ring-cyan-400" />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Email</Label>
                  <Input required type="email" value={form.email} onChange={set("email")} data-testid="booking-email" className="bg-[#050505] border-white/15 h-11 rounded-sm focus-visible:ring-cyan-400" />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Company</Label>
                  <Input value={form.company} onChange={set("company")} data-testid="booking-company" className="bg-[#050505] border-white/15 h-11 rounded-sm focus-visible:ring-cyan-400" />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Service</Label>
                  <select required value={form.service} onChange={set("service")} data-testid="booking-service" className="w-full bg-[#050505] border border-white/15 h-11 rounded-sm px-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-400">
                    <option value="">Select…</option>
                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Preferred Date</Label>
                  <Input required type="date" min={today} value={form.date} onChange={set("date")} data-testid="booking-date" className="bg-[#050505] border-white/15 h-11 rounded-sm focus-visible:ring-cyan-400 [color-scheme:dark]" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Time Slot</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {SLOTS.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => set("time")(s)}
                        data-testid={`booking-slot-${s}`}
                        className={`h-10 rounded-sm border font-mono text-xs transition-colors ${
                          form.time === s ? "bg-cyan-400 text-black border-cyan-400" : "bg-[#050505] text-white/70 border-white/15 hover:border-cyan-400/50"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Notes (optional)</Label>
                  <Textarea rows={3} value={form.message} onChange={set("message")} data-testid="booking-message" className="bg-[#050505] border-white/15 rounded-sm focus-visible:ring-cyan-400 resize-none" />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={loading} data-testid="booking-submit" className="w-full bg-cyan-400 hover:bg-white text-black rounded-full h-12 font-mono text-[11px] uppercase tracking-[0.2em]">
                    {loading ? "Requesting…" : "Request Consultation"}
                    <ArrowUpRight size={16} className="ml-2" />
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </BookingCtx.Provider>
  );
};
